'use client';
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../hooks/useTheme';
import { DollarSignIcon, ActivityIcon, GlobeIcon, ZapIcon, TrendingUp, Sparkles, ArrowUpRight } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Enhanced particle background with depth
function StatsParticleBackground() {
  const canvasRef = useRef(null);
  const { isDark } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationId;

     const resize = () => {
    const dpr = Math.min(window.devicePixelRatio, 1.5);
    canvas.width = window.innerWidth * dpr;
    canvas.height = 400 * dpr;
    ctx.scale(dpr, dpr);
    };

    resize();
    window.addEventListener('resize', resize);

    const particles = [];
    for (let i = 0; i < 25; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        radius: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.5 + 0.2,
        hue: Math.random() * 60 + 170 // Cyan to blue range
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        // Draw connections
        particles.slice(i + 1).forEach(p2 => {
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 90) {
            ctx.save();
            ctx.globalAlpha = (1 - dist / 120) * 0.15;
            ctx.strokeStyle = isDark ? 'rgba(6, 182, 212, 0.3)' : 'rgba(6, 182, 212, 0.2)';
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
            ctx.restore();
          }
        });

        // Draw particle
        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.shadowColor = `hsla(${p.hue}, 70%, 60%, 0.6)`;
        ctx.fillStyle = `hsla(${p.hue}, 70%, 60%, 0.4)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, [isDark]);

  return (
    <div className="absolute inset-0 -z-10">
      <canvas ref={canvasRef} className="w-full h-full opacity-30" />
    </div>
  );
}

// Floating orbs background
function FloatingOrbs({ isDark }) {
  const orbsRef = useRef([]);
  
  useEffect(() => {
    orbsRef.current.forEach((orb, i) => {
      if (orb) {
        gsap.to(orb, {
          x: `random(-80, 80)`,
          y: `random(-80, 80)`,
          scale: `random(0.9, 1.3)`,
          duration: `random(8, 12)`,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
          delay: i * 1.5
        });
      }
    });
  }, []);
  
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
      <div
        ref={el => orbsRef.current[0] = el}
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-2xl"
        style={{
          background: isDark
            ? 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(6, 182, 212, 0.1) 0%, transparent 70%)'
        }}
      />
      <div
        ref={el => orbsRef.current[1] = el}
        className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-2xl"
        style={{
          background: isDark
            ? 'radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, transparent 70%)'
        }}
      />
    </div>
  );
}

function RealisticStatCard({ 
  icon: Icon, 
  value, 
  prefix, 
  suffix, 
  label, 
  index,
  description,
  trend
}) {
  const { isDark } = useTheme();
  const cardRef = useRef(null);
  const numberRef = useRef(null);
  const glowRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!cardRef.current || !numberRef.current) return;

    // Entrance animation with 3D effect
    

    // Realistic counter animation
    let current = 0;
    const target = value;
    const duration = 2.5;
    const startTime = Date.now();

    const animateCounter = () => {
    let start = 0;
    const duration = 2000;
    const step = target / (duration / 16);

    const update = () => {
      start += step;
      if (start < target) {
        numberRef.current.textContent =
          `${prefix || ''}${suffix === '%' ? start.toFixed(3) : Math.floor(start).toLocaleString()}${suffix || ''}`;
        requestAnimationFrame(update);
      } else {
        numberRef.current.textContent =
          `${prefix || ''}${suffix === '%' ? target.toFixed(3) : target.toLocaleString()}${suffix || ''}`;
      }
    };
    update();
};

    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateCounter();
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.3 });
    
    observer.observe(cardRef.current);

    return () => observer.disconnect();
  }, [value, prefix, suffix, index]);

  const handleMouseMove = (e) => {
    if (!cardRef.current || !glowRef.current) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    gsap.to(glowRef.current, {
      background: isDark
        ? `radial-gradient(circle at ${x}% ${y}%, rgba(6, 182, 212, 0.1), transparent 60%)`
        : `radial-gradient(circle at ${x}% ${y}%, rgba(6, 182, 212, 0.08), transparent 60%)`,
      duration: 0.3
    });
  };

  return (
    <motion.div 
      className="group col-span-1"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
    >
      <div 
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`relative rounded-3xl p-8 lg:p-10 h-full transition-transform duration-500 cursor-pointer backdrop-blur-md overflow-hidden ${
          isDark
             ? 'bg-gradient-to-br from-slate-900/95 to-slate-800/95 border border-white/[0.05]'
             : 'bg-gradient-to-br from-white to-slate-50/80 border border-slate-200/60'
        }`}
        style={{ 
          transformStyle: 'preserve-3d',
          transform: isHovered ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)'
        }}
      >
        {/* Dynamic glow effect */}
        <div
          ref={glowRef}
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        />

        {/* Top gradient line */}
        <div className={`absolute top-0 left-0 right-0 h-px transition-opacity duration-500 ${
          isDark 
            ? 'bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent opacity-0 group-hover:opacity-100'
            : 'bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent opacity-0 group-hover:opacity-100'
        }`} />

        {/* Floating accent */}
        <div className={`absolute top-6 right-6 w-20 h-20 rounded-full transition-transform duration-500 ${
          isDark ? 'bg-cyan-400/10 group-hover:bg-cyan-400/20' : 'bg-cyan-500/10 group-hover:bg-cyan-500/20'
        }`} style={{ filter: 'blur(13px)' }} />

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <motion.div 
            className={`relative p-5 rounded-2xl shadow-lg transition-transform duration-500 ${
              isDark 
                ? 'bg-gradient-to-br from-cyan-500/10 to-blue-500/10 group-hover:from-cyan-500/20 group-hover:to-blue-500/20 shadow-cyan-500/20'
                : 'bg-gradient-to-br from-cyan-100 to-blue-100 group-hover:from-cyan-200 group-hover:to-blue-200 shadow-cyan-500/10'
            }`}
            whileHover={{ scale: 1.15, rotate: 5 }}
            transition={{ type: 'spring', stiffness: 400, damping: 10 }}
          >
            <Icon className={`w-10 h-10 lg:w-12 lg:h-12 ${
              isDark ? 'text-cyan-400' : 'text-cyan-600'
            }`} />
            
            {/* Icon glow */}
            <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
              isDark ? 'bg-cyan-400/20' : 'bg-cyan-500/20'
            }`} style={{ filter: 'blur(5px)' }} />
          </motion.div>
        </div>

        {/* Number */}
        <div className="text-center mb-4">
          <div 
            ref={numberRef}
            className={`text-3xl lg:text-3xl xl:text-4xl font-black tracking-tight leading-none mb-2 ${
              isDark 
                ? 'text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-200' 
                : 'text-transparent bg-clip-text bg-gradient-to-r from-slate-900 to-slate-700'
            }`}
            style={{ 
              letterSpacing: '-0.03em'
            }}
          >
            0
          </div>
          
          {/* Trend indicator */}
          {trend && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${
                isDark
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                  : 'bg-emerald-100 text-emerald-700 border border-emerald-300'
              }`}
            >
              <TrendingUp className="w-3 h-3" />
              {trend}
            </motion.div>
          )}
        </div>

        {/* Label */}
        <p className={`text-base lg:text-lg font-bold text-center tracking-wide uppercase mb-3 ${
          isDark 
            ? 'text-slate-400 group-hover:text-slate-300' 
            : 'text-slate-600 group-hover:text-slate-700'
        }`}>
          {label}
        </p>

        {/* Description */}
        {description && (
          <p className={`text-sm text-center leading-relaxed ${
            isDark ? 'text-slate-500' : 'text-slate-500'
          }`}>
            {description}
          </p>
        )}

        {/* Bottom gradient bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-3xl overflow-hidden">
          <div className={`h-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700 ease-out ${
            isDark 
              ? 'bg-gradient-to-r from-cyan-500 via-blue-500 to-violet-500' 
              : 'bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400'
          }`} />
        </div>

        {/* Sparkle effect on hover */}
        <motion.div
          className="absolute top-4 left-4"
          initial={{ opacity: 0, scale: 0 }}
          animate={isHovered ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Sparkles className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
        </motion.div>
      </div>
    </motion.div>
  );
}

export function Stats() {
  const { isDark } = useTheme();
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const badgeRef = useRef(null);
  const subtitleRef = useRef(null);

  useEffect(() => {
    if (!titleRef.current || !badgeRef.current || !subtitleRef.current) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        start: 'top 80%',
        toggleActions: 'play none none none',
        once: true 
      }
    });

    // Badge entrance
    tl.fromTo(
      badgeRef.current,
      { opacity: 0, y: -20, scale: 0.8 },
      { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: 'back.out(1.7)' }
    );

    // Title words stagger
    const titleWords = titleRef.current.querySelectorAll('.title-word');
    tl.fromTo(
      titleWords,
      { y: 40, opacity: 0, rotationX: -20 },
      { 
        y: 0, 
        opacity: 1, 
        rotationX: 0,
        stagger: 0.1, 
        duration: 0.8, 
        ease: 'power3.out' 
      },
      '-=0.3'
    );

    // Subtitle
    tl.fromTo(
      subtitleRef.current,
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out' },
      '-=0.4'
    );

    // Floating animation for badge
    gsap.to(badgeRef.current, {
      y: -5,
      duration: 2,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut'
    });
  }, []);

  const statsData = [
    {
      icon: DollarSignIcon,
      value: 12,
      prefix: '$',
      suffix: 'B+',
      label: 'Processed Annually',
      description: 'Total transaction volume',
      trend: '+23% YoY'
    },
    {
      icon: GlobeIcon,
      value: 190,
      suffix: '+',
      label: 'Countries Supported',
      description: 'Global payment coverage',
      trend: '+15 this year'
    },
    {
      icon: ActivityIcon,
      value: 99.999,
      suffix: '%',
      label: 'Uptime SLA',
      description: 'Guaranteed availability',
      trend: '99.999%'
    },
    {
      icon: ZapIcon,
      value: 45,
      prefix: '<',
      suffix: 'ms',
      label: 'Average Latency',
      description: 'Lightning fast processing',
      trend: 'Industry leading'
    }
  ];

  return (
    <section 
      ref={containerRef}
      className="relative py-24 lg:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden"
    >
      {/* Backgrounds */}
      <StatsParticleBackground />
      <FloatingOrbs isDark={isDark} />
      
      {/* Grid overlay */}
      <div 
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: isDark 
            ? 'linear-gradient(rgba(6, 182, 212, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.3) 1px, transparent 1px)'
            : 'linear-gradient(rgba(148, 163, 184, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 0.3) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20 lg:mb-28">
          <motion.div
            ref={badgeRef}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold tracking-wide uppercase mb-8 transition-transform duration-300 cursor-pointer ${
              isDark 
                ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 text-cyan-400 hover:from-cyan-500/20 hover:to-blue-500/20 hover:border-cyan-500/30' 
                : 'bg-gradient-to-r from-cyan-100 to-blue-100 border border-cyan-300 text-cyan-700 hover:from-cyan-200 hover:to-blue-200'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            By The Numbers
            <ArrowUpRight className="w-4 h-4" />
          </motion.div>
          
          <div ref={titleRef} className="mb-6">
            <h2 className={`text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-tight ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>
              <span className="title-word block mb-2">
                Trusted by
              </span>
              <span className={`title-word block ${
                isDark
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400'
                  : 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-violet-600'
              }`}>
                Industry Leaders
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-violet-500/20 blur-2xl -z-10" />
              </span>
            </h2>
          </div>
          
          <p 
            ref={subtitleRef}
            className={`text-lg lg:text-xl max-w-3xl mx-auto font-medium leading-relaxed ${
              isDark ? 'text-slate-400' : 'text-slate-600'
            }`}
          >
            Powering ambitious businesses worldwide with{' '}
            <span className={`font-bold ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
              reliable infrastructure
            </span>
            {' '}and unmatched performance
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {statsData.map((stat, index) => (
            <RealisticStatCard
              key={stat.label}
              {...stat}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default Stats;